/**
 * Everything a comanda card can change.
 *
 * Kept apart from the card so the same actions are available wherever the
 * comanda is rendered — in its card on the board, and in the full-screen view
 * opened from its header. A cook must never have to close a comanda to reach a
 * button (FR-011a), and an action must behave identically wherever it is
 * pressed (FR-011b), which is what sharing this hook guarantees.
 */
import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { itemsApi, ordersApi } from "../api/client";
import { STATUS_CYCLE } from "../lib/order-card";
import { refreshOrders } from "../lib/sync/refresh-orders";

// i18n-keep: order item status codes compared with the payload, never shown
const RESTARTABLE = new Set(["PREPARING", "IN_PROGRESS", "PENDING", "CONFIRMED"]);

export interface OrderActions {
  /** Ids of dishes with a request in flight, so a double press cannot land. */
  loadingItemIds: Set<string>;
  loadingAction: "fire" | "complete" | null;
  /**
   * Advances a line. `portions` are the order items behind it: a collapsed line
   * of identical plates moves them together, and one request goes out per row.
   */
  advanceItem: (item: any, portions?: readonly any[]) => Promise<void>;
  fireNextWave: () => Promise<void>;
  markAllReady: (items: any[]) => Promise<void>;
  completeOrder: () => Promise<void>;
  cancelOrder: () => Promise<void>;
}

export function useOrderActions(order: any): OrderActions {
  const queryClient = useQueryClient();
  const [loadingItemIds, setLoadingItemIds] = useState<Set<string>>(new Set());
  const [loadingAction, setLoadingAction] = useState<"fire" | "complete" | null>(null);

  const invalidateOrders = useCallback(() => {
    refreshOrders(queryClient);
  }, [queryClient]);

  const advanceItem = useCallback(
    async (item: any, portions?: readonly any[]) => {
      const next = STATUS_CYCLE[item?.status];
      if (!next) return; // READY is the chef's last word; SERVED is the waiter's

      // A collapsed line wears the LEAST advanced status of its plates, so
      // taking only the rows sitting at that status leaves the ones already
      // further along where they are: one press can never walk a plate back.
      const behind = (portions && portions.length > 0 ? portions : [item]).filter(
        (p) => p?.status === item.status,
      );
      const ids = [...new Set(behind.map((p) => p.id))];
      if (ids.length === 0) return;
      if (ids.some((id) => loadingItemIds.has(id))) return;

      setLoadingItemIds((prev) => {
        const busy = new Set(prev);
        for (const id of ids) busy.add(id);
        return busy;
      });
      try {
        for (const id of ids) {
          if (item.status === "PREPARING") await itemsApi.bump(id);
          else await itemsApi.updateStatus(id, next);
        }
        invalidateOrders();
      } finally {
        setLoadingItemIds((prev) => {
          const rest = new Set(prev);
          for (const id of ids) rest.delete(id);
          return rest;
        });
      }
    },
    [invalidateOrders, loadingItemIds],
  );

  const fireNextWave = useCallback(async () => {
    if (loadingAction) return;
    setLoadingAction("fire");
    try {
      await ordersApi.fireCourse(order.id);
      invalidateOrders();
    } finally {
      setLoadingAction(null);
    }
  }, [invalidateOrders, loadingAction, order?.id]);

  const markAllReady = useCallback(
    async (items: any[]) => {
      if (loadingAction) return;
      // Only dishes the waiter has actually sent in. A wave nobody has called
      // is not the kitchen's to finish.
      const targets = items.filter((i) => !!i?.firedAt && RESTARTABLE.has(i?.status));
      if (targets.length === 0) return;

      setLoadingAction("complete");
      try {
        await Promise.all(
          targets.map((i) =>
            i.status === "PREPARING" || i.status === "IN_PROGRESS"
              ? itemsApi.bump(i.id)
              : itemsApi.updateStatus(i.id, "READY"),
          ),
        );
        invalidateOrders();
      } finally {
        setLoadingAction(null);
      }
    },
    [invalidateOrders, loadingAction],
  );

  const completeOrder = useCallback(async () => {
    if (loadingAction) return;
    setLoadingAction("complete");
    try {
      await ordersApi.complete(order.id);
      invalidateOrders();
    } finally {
      setLoadingAction(null);
    }
  }, [invalidateOrders, loadingAction, order?.id]);

  const cancelOrder = useCallback(async () => {
    await ordersApi.update(order.id, {
      status: "CANCELLED",
      cancelReason: "orphan_kitchen",
    });
    refreshOrders(queryClient);
  }, [order?.id, queryClient]);

  return {
    loadingItemIds,
    loadingAction,
    advanceItem,
    fireNextWave,
    markAllReady,
    completeOrder,
    cancelOrder,
  };
}
